import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Bug, HelpCircle, Menu, X } from 'lucide-react'
import { ColorModeButton } from '../../components/ui/color-mode'
import { useIsMobile } from '../../hooks/use-mobile'
import { Button } from '../../components/tailwind/button'
import { openReportDialog } from '../../store/reportSlice'
import ReportIssueDialog from '../../components/shared/dialogs/ReportIssueDialog'
import { cn } from '../../lib/utils'
import { WalletBalancePill } from '../../components/whatsapp/billing/wallet/WalletBalancePill'

const Header = (props) => {
    const { open, handleDrawerOpen, handleDrawerClose } = props;
    const dispatch = useDispatch();
    const pageTitle = useSelector(state => state.navigation.pageTitle);
    const isMobile = useIsMobile();
    const [helpOpen, setHelpOpen] = useState(false);
    const helpRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!helpRef.current) return;
            if (!helpRef.current.contains(e.target)) setHelpOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setHelpOpen(false);
        };
        document.addEventListener('click', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    return (
        <header
            className={cn(
                "relative z-20 w-full shrink-0",
                "flex items-center justify-between gap-3",
                "h-[50px] sm:h-[60px] px-3 sm:px-4",
                "bg-white dark:bg-slate-900",
                "border-b border-gray-200 dark:border-slate-800",
                "text-gray-900 dark:text-white"
            )}
        >
            <div className="flex items-center min-w-0 flex-1 gap-2">
                {isMobile && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="shrink-0 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800"
                        onClick={open ? handleDrawerClose : handleDrawerOpen}
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </Button>
                )}
                {pageTitle && (
                    <h1 className="text-base sm:text-lg font-semibold truncate text-gray-900 dark:text-white">
                        {pageTitle}
                    </h1>
                )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
                <WalletBalancePill />
                <Button
                    size="sm"
                    variant="destructive"
                    className="gap-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950/60"
                    onClick={() => dispatch(openReportDialog())}
                >
                    <Bug className="h-4 w-4" />
                    {!isMobile && "Report Issue"}
                </Button>

                <div className="relative" ref={helpRef}>
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 border border-blue-200 dark:bg-slate-900 dark:text-blue-400 dark:border-slate-700 dark:hover:bg-slate-800"
                        onClick={() => setHelpOpen(!helpOpen)}
                    >
                        <HelpCircle className="h-4 w-4" />
                        {!isMobile && "Help & Support"}
                    </Button>

                    {helpOpen && (
                        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-30 p-4">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Need assistance?</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Our team is here to help you succeed.</p>
                            <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                                <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Email Support Directly:</p>
                                <a
                                    href="mailto:support@prodyt.com"
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium flex items-center gap-2"
                                >
                                    <span>📧</span>
                                    support@prodyt.com
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                <ColorModeButton size={isMobile ? "sm" : "md"} />
            </div>

            <ReportIssueDialog />
        </header>
    )
}

export default Header
